import { uniqueId } from 'lodash';

export interface ChatMessage {
  _id: string;
  text?: string;
  createdAt: Date;
  user?: { _id: any; avatar?: string; name?: string };
  system?: boolean;
  [key: string]: any;
}

/**
 * Generate a fingerprint for the current chat to detect when it changes
 */
export const getChatFingerprint = (
  chatMessages: ChatMessage[],
  globalUser: any
): string | null => {
  if (!chatMessages.length) return null;

  const userMessages = chatMessages.filter(
    (msg) =>
      msg.user &&
      msg.user._id === globalUser &&
      !msg.system &&
      msg.text &&
      msg.text.trim().length > 0
  );

  const assistantMessages = chatMessages.filter(
    (msg) =>
      msg.user &&
      (msg.user._id !== globalUser || msg.system) &&
      msg.text &&
      msg.text.trim().length > 0 &&
      !msg.formSchema &&
      !msg.actionCards &&
      !msg.showProfileForm &&
      !msg.showOnboardingForm
  );

  if (userMessages.length === 0 || assistantMessages.length === 0) return null;

  const firstUserMsg = userMessages[0];
  const lastUserMsg = userMessages[userMessages.length - 1];
  const firstAssistantMsg = assistantMessages[0];
  const lastAssistantMsg = assistantMessages[assistantMessages.length - 1];

  return `${userMessages.length}-${assistantMessages.length}-${firstUserMsg._id}-${lastUserMsg._id}-${firstAssistantMsg._id}-${lastAssistantMsg._id}`;
};

/**
 * Check if there are meaningful chat messages to save
 */
export const hasChatToSave = (
  chatMessages: ChatMessage[],
  globalUser: any
): boolean => {
  return getChatFingerprint(chatMessages, globalUser) !== null;
};

/**
 * Check if save prompt was dismissed for current chat
 */
export const isSavePromptDismissed = (
  chatMessages: ChatMessage[],
  globalUser: any,
  dismissedSavePrompts: string[]
): boolean => {
  const currentFingerprint = getChatFingerprint(chatMessages, globalUser);
  if (!currentFingerprint) return false;
  return dismissedSavePrompts.includes(currentFingerprint);
};

/**
 * Parse inline data references inserted by the "/" data selector.
 * Format: `[@data:${id} "${title}"]`
 */
export const parseInlineDataRefs = (
  text: string
): { dataIds: string[]; cleanedText: string } => {
  if (!text) return { dataIds: [], cleanedText: text };

  const regex = /\[@data:([a-fA-F0-9]{6,64})\s+"[^"]*"\]/g;
  const dataIds: string[] = [];
  let cleanedText = text;

  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const id = match[1];
    if (id && !dataIds.includes(id)) dataIds.push(id);
  }

  if (dataIds.length > 0) {
    cleanedText = cleanedText.replace(regex, '').replace(/\s{2,}/g, ' ').trim();
  }

  return { dataIds, cleanedText };
};

/**
 * Reference types supported by the slash command
 */
export type ReferenceType = 'data' | 'item' | 'project' | 'calendar';

/**
 * Parsed reference from text
 */
export interface ParsedReference {
  type: ReferenceType;
  id: string;
  title: string;
  startIndex: number;
  endIndex: number;
  fullMatch: string;
}

/**
 * Parse all reference types from text and return segments
 * Format: `[@data:${id} "${title}"]`, `[@item:${id} "${title}"]`, etc.
 */
export const parseAllReferences = (text: string): Array<{ type: 'text' | 'reference'; content: string; reference?: ParsedReference }> => {
  if (!text) return [{ type: 'text', content: text }];

  // Match all reference types: [@data:id "title"], [@item:id "title"], [@project:id "title"], [@calendar:id "title"]
  const referenceRegex = /\[@(data|item|project|calendar):([a-fA-F0-9]{6,64})\s+"([^"]*)"\]/g;
  
  const segments: Array<{ type: 'text' | 'reference'; content: string; reference?: ParsedReference }> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = referenceRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      const textContent = text.substring(lastIndex, match.index);
      if (textContent) {
        segments.push({ type: 'text', content: textContent });
      }
    }

    // Add the reference
    const reference: ParsedReference = {
      type: match[1] as ReferenceType,
      id: match[2],
      title: match[3],
      startIndex: match.index,
      endIndex: match.index + match[0].length,
      fullMatch: match[0],
    };

    segments.push({ type: 'reference', content: match[0], reference });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    const textContent = text.substring(lastIndex);
    if (textContent) {
      segments.push({ type: 'text', content: textContent });
    }
  }

  // If no references found, return the whole text as a single segment
  if (segments.length === 0) {
    segments.push({ type: 'text', content: text });
  }

  return segments;
};

/**
 * Create display text with references replaced by placeholder spaces
 * This allows the TextInput to maintain correct cursor position while hiding reference text
 */
export const createDisplayTextWithPlaceholders = (text: string): string => {
  if (!text) return text;
  
  const referenceRegex = /\[@(data|item|project|calendar):([a-fA-F0-9]{6,64})\s+"([^"]*)"\]/g;
  
  // Replace each reference with spaces (maintains width for positioning)
  return text.replace(referenceRegex, (match) => {
    // Use spaces equal to the length of the reference pattern
    // This maintains approximate width for positioning
    return ' '.repeat(match.length);
  });
};

/**
 * Add disclaimer message to chat
 */
export const addDisclaimer = (
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
) => {
  setChatMessages((previous) => [
    ...previous,
    {
      _id: uniqueId(),
      text: 'AI generated responses are not always accurate, please use caution when using this feature.',
      isDisclaimer: true,
      system: true,
      createdAt: new Date(),
      user: { _id: 'AI' },
    },
  ]);
};


